if (!customElements.get('sn-localization-sheet')) {
  customElements.define(
    'sn-localization-sheet',
    class SNLocalizationSheet extends HTMLElement {
      connectedCallback() {
        if (this.parentElement !== document.body) {
          const duplicate = document.body.querySelector(`#${CSS.escape(this.id)}`);
          if (duplicate && duplicate !== this) duplicate.remove();
          document.body.appendChild(this);
          return;
        }

        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.events = new AbortController();
        const listenOptions = { signal: this.events.signal };

        this.form = this.querySelector('form');
        this.dialog = this.querySelector('[data-sn-localization-dialog]');
        this.countryInput = this.querySelector('input[name="country_code"]');
        this.languageInput = this.querySelector('input[name="locale_code"]');
        this.doneButton = this.querySelector('[data-sn-localization-done]');
        this.closeElements = this.querySelectorAll('[data-sn-localization-close]');
        this.lastOpener = null;

        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onOptionClick = this.handleOptionClick.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onDone = this.applySelection.bind(this);
        this.onSectionUnload = this.handleSectionUnload.bind(this);
        this.onPageShow = () => { this.syncFromServer(); window.SNMarket?.schedule(); };

        document.addEventListener('click', this.onDocumentClick, listenOptions);
        this.addEventListener('click', this.onOptionClick, listenOptions);
        this.addEventListener('keydown', this.onKeyDown, listenOptions);
        this.doneButton?.addEventListener('click', this.onDone, listenOptions);
        this.closeElements.forEach((element) => element.addEventListener('click', () => this.close(false), listenOptions));
        document.addEventListener('shopify:section:unload', this.onSectionUnload, listenOptions);
        window.addEventListener('pageshow', this.onPageShow, listenOptions);

        this.syncFromServer();
      }

      disconnectedCallback() {
        if (this.parentElement === document.body) return;
        this.events?.abort();
        delete this.dataset.initialized;
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('shopify:section:unload', this.onSectionUnload);
        window.removeEventListener('pageshow', this.onPageShow);
        document.body.classList.remove('sn-localization-open');
      }

      handleSectionUnload(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.remove();
      }

      handleDocumentClick(event) {
        const opener = event.target.closest(`[data-sn-localization-open="${CSS.escape(this.id)}"]`);
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      }

      handleOptionClick(event) {
        const option = event.target.closest('[data-sn-localization-option]');
        if (!option || !this.contains(option)) return;
        event.preventDefault();

        const type = option.dataset.snLocalizationOption;
        const input = type === 'country' ? this.countryInput : this.languageInput;
        if (!input) return;

        input.value = option.dataset.value;
        this.renderSelection(type, option.dataset.value);
      }

      handleKeyDown(event) {
        if (!this.classList.contains('is-open')) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(false);
          return;
        }

        if (event.key !== 'Tab' || !this.dialog) return;
        const focusable = Array.from(
          this.dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
        ).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      syncFromServer() {
        this.confirmedCountry = this.dataset.currentCountry || this.countryInput?.value || '';
        this.confirmedLanguage = this.dataset.currentLanguage || this.languageInput?.value || '';
        this.resetPending();
        if (this.countryInput) this.countryInput.disabled = false;
        if (this.languageInput) this.languageInput.disabled = false;
        if (this.doneButton) {
          this.doneButton.disabled = false;
          this.doneButton.removeAttribute('aria-busy');
        }
      }

      resetPending() {
        if (this.countryInput) this.countryInput.value = this.confirmedCountry;
        if (this.languageInput) this.languageInput.value = this.confirmedLanguage;
        this.renderSelection('country', this.confirmedCountry);
        this.renderSelection('language', this.confirmedLanguage);
      }

      renderSelection(type, value) {
        this.querySelectorAll(`[data-sn-localization-option="${type}"]`).forEach((option) => {
          const selected = option.dataset.value === value;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      }

      open(opener) {
        this.lastOpener = opener;
        window.SNMarket?.schedule();
        this.resetPending();
        this.hidden = false;
        void this.offsetWidth;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        opener.setAttribute('aria-expanded', 'true');
        document.body.classList.add('sn-localization-open');
        this.dialog?.focus({ preventScroll: true });
      }

      close(restoreFocus = true) {
        this.resetPending();
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sn-localization-open');
        this.lastOpener?.setAttribute('aria-expanded', 'false');

        const opener = this.lastOpener;
        window.setTimeout(() => {
          if (!this.classList.contains('is-open')) this.hidden = true;
        }, 320);

        if (restoreFocus) opener?.focus({ preventScroll: true });
      }

      applySelection(event) {
        event.preventDefault();
        if (!this.form) return;

        const selectedCountry = this.querySelector('[data-sn-localization-option="country"][aria-pressed="true"]');
        const displayCurrency = window.SNMarket?.state.displayCurrency;
        const countryChanged = this.countryInput && (this.countryInput.value !== this.confirmedCountry ||
          (displayCurrency && selectedCountry?.dataset.currency && selectedCountry.dataset.currency !== displayCurrency));
        const languageChanged = this.languageInput && this.languageInput.value !== this.confirmedLanguage;

        if (!countryChanged && !languageChanged) {
          this.close(true);
          return;
        }

        let returnInput = this.form.querySelector('input[name="return_to"]');
        if (!returnInput) {
          returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          this.form.appendChild(returnInput);
        }
        returnInput.value = `${window.location.pathname}${window.location.search}${window.location.hash}`;

        // Shopify's localization endpoint accepts country_code and locale_code.
        // Send the selected pair together; omitting one field can restore an old
        // preference during an automatic app redirect or a restored page session.
        if (this.countryInput) this.countryInput.disabled = false;
        if (this.languageInput) this.languageInput.disabled = false;

        this.doneButton.disabled = true;
        this.doneButton.setAttribute('aria-busy', 'true');
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.submit();
      }
    }
  );
}
